import './Cluster.scss';
import React, { FC, Suspense, lazy } from 'react';
import { useQuery } from '@apollo/client';
import { GetClusterQuery } from '@/generated/graphql';
import GetCluster from './graphql/Cluster.gql';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import { getPlaceholderDataAttributes } from '@/components/edifice/Theatre/Theatre';
import Panel from '@/components/stage/Panel/Panel';
import Loading from '@/components/basic/Loading/Loading';
import type { ClusterCreateObject } from './clusterCreateObject';
import clusterCreateObject from './clusterCreateObject';
import classNames from 'classnames';

const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);
const ClusterEdit = lazy(
    () =>
        import(/* webpackChunkName: "edit-main" */ './ClusterEdit/ClusterEdit'),
);

type ClusterProps = {
    cluster: ClusterCreateObject | number;
};

const Cluster: FC<ClusterProps> = ({ cluster }) => {
    const { userCurrent } = useUserCurrent();

    let clusterObject: ClusterCreateObject;

    // (!) using skip to avoid calling useQuery hook conditionally
    //     The hook is only needed:
    //     IF cluster is a clusterId
    //     ELSE cluster has already been fetched and is already a ClusterObject.
    //          This is the case when the <Cluster/> is used from <Matrix/>,
    //          which already fetches the clusters.
    //     When skip = true, then data is always false, even after loading becomes false.
    const { loading, error, data } = useQuery<GetClusterQuery>(GetCluster, {
        variables: { clusterId: cluster },
        skip: typeof cluster !== 'number',
    });

    if (loading) {
        return <Loading type="element" />;
    }

    if (error || (!data && typeof cluster === 'number')) {
        return <pre>Error...{JSON.stringify(error, null, 2)}</pre>;
    }
    if (data) {
        clusterObject = clusterCreateObject(data.cluster);
    } else if (typeof cluster !== 'number') {
        clusterObject = cluster;
    }

    const currentNodes = [];
    const panels = clusterObject.panels.map((panel, index) => {
        currentNodes.push({
            nodeId: panel.panel.id,
            adminTitle: panel.panel.adminTitle,
        });
        return (
            <Panel
                key={`panel${index}`}
                panel={panel.panel}
                panelStyles={panel.panelStyles}
                index={index}
            />
        );
    });

    const placeholderDataAttributes = getPlaceholderDataAttributes(
        clusterObject.clusterFields.shape === 'PLACEHOLDER',
        'cluster',
        clusterObject.clusterFields.id,
        clusterObject.clusterFields.adminTitle,
        clusterObject.clusterFields.adminNote,
    );

    const clusterClassName = classNames(
        'Cluster',
        `Cluster-${clusterObject.clusterFields.shape.toLowerCase()}`,
        {
            NewComponentPlaceholder:
                clusterObject.clusterFields.shape === 'PLACEHOLDER',
        },
    );

    const clusterStyle = clusterObject.clusterStyles;

    const clusterNonEditContent = (
        <section
            className={clusterClassName}
            style={clusterStyle}
            {...placeholderDataAttributes}
        >
            {panels}
        </section>
    );

    if (userCurrent?.isEditMode === 'CLUSTER') {
        const nodeIds = currentNodes.map((node) => node.nodeId);

        const passEditState = {
            editKey: clusterObject.clusterFields.id,
            componentType: 'CLUSTER',
            fields: {
                adminTitle: clusterObject.clusterFields.adminTitle,
                adminNote: clusterObject.clusterFields.adminNote,
                published: clusterObject.clusterFields.published,
                publishAll: false,
            },
            localFields: {
                shape: clusterObject.clusterFields.shape,
            },
            clusterId: clusterObject.clusterFields.id,
            ownerId: clusterObject.clusterFields.owner.id,

            // needed for deleting and sorting panels
            currentNodes: currentNodes,

            // Only needed for Adding a new panel to the cluster
            newPanelKind: null, // set by AddPanel.tsx
            newPanelFields: {}, // filled by AddPanel.tsx
            newMediaFields: {}, // filled by AddPanelXxxx.tsx
            hasRequiredMediaFieldChanges: false,

            // for deleting panels
            nodesFlaggedForRemoval: [],

            // for sorting panels
            nodeIds: nodeIds, // must remain unchanged for comparison.
            sortedNodeIds: nodeIds,
            hasResortedNodes: false,
        };
        return (
            <Suspense fallback={clusterNonEditContent}>
                <EditShell passEditState={passEditState}>
                    <ClusterEdit
                        clusterObject={clusterObject}
                        panels={panels}
                        clusterClassName={clusterClassName}
                        clusterStyle={clusterStyle}
                        placeholderDataAttributes={placeholderDataAttributes}
                    />
                </EditShell>
            </Suspense>
        );
    }

    return clusterNonEditContent;
};

export default Cluster;
